import Swal from "sweetalert2";
import WebService from "./WebService";

export type WoodQuality = { id: number; name: string };
export type ProductCategoryOption = { id: number; name: string };
export type CategoryQualityPrice = { category_id: number; price: number };

export function mapWoodQualities(res: any): WoodQuality[] {
  const arr = Array.isArray(res?.data)
    ? res.data
    : Array.isArray(res?.data?.data)
      ? res.data.data
      : [];
  return arr
    .map((q: any) => ({
      id: Number(q.id ?? q.quality_id),
      name: String(q.name ?? q.quality_name ?? q.label ?? "").trim(),
    }))
    .filter((q: WoodQuality) => q.id && q.name);
}

export function mapProductCategories(res: any): ProductCategoryOption[] {
  const raw = res?.data;
  const arr = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.data)
      ? raw.data
      : Array.isArray(raw?.categories)
        ? raw.categories
        : [];
  return arr
    .map((c: any) => ({
      id: Number(c.category_id ?? c.id),
      name: String(c.category_name ?? c.name ?? "").trim(),
    }))
    .filter((c: ProductCategoryOption) => c.id && c.name);
}

export async function fetchWoodQualities(): Promise<WoodQuality[]> {
  try {
    const res: any = await WebService.getAPI({
      action: "/v1/door-management/qualities",
      isShowError: false,
    });
    return mapWoodQualities(res);
  } catch {
    try {
      const res: any = await WebService.getAPI({
        action: "/v1/door-management/users/qualities",
        isShowError: false,
      });
      return mapWoodQualities(res);
    } catch {
      return [];
    }
  }
}

export async function fetchProductCategories(): Promise<ProductCategoryOption[]> {
  const res: any = await WebService.getAPI({
    action: "/v1/door-management/product/categories",
  });
  return mapProductCategories(res);
}

export async function createWoodQuality(body: {
  name: string;
  category_id?: number;
  price?: number;
  category_prices?: CategoryQualityPrice[];
}): Promise<WoodQuality> {
  const payload: any = { name: body.name };
  if (body.category_prices && body.category_prices.length > 0) {
    payload.category_prices = body.category_prices;
  }
  if (body.category_id != null) payload.category_id = body.category_id;
  if (body.price != null) payload.price = body.price;
  const res: any = await WebService.postAPI({
    action: "/v1/door-management/qualities",
    body: payload,
  });
  const q = res?.data || {};
  return {
    id: Number(q.id ?? q.quality_id),
    name: String(q.name ?? q.quality_name ?? body.name),
  };
}

export async function updateWoodQuality(id: number, name: string): Promise<WoodQuality> {
  const res: any = await WebService.putAPI({
    action: `/v1/door-management/qualities/${id}`,
    body: { name },
  });
  const q = res?.data || {};
  return {
    id: Number(q.id ?? q.quality_id ?? id),
    name: String(q.name ?? q.quality_name ?? name),
  };
}

export async function deleteWoodQuality(id: number): Promise<void> {
  await WebService.deleteAPI({
    action: `/v1/door-management/qualities/${id}`,
  });
}

export async function promptWoodQualityName(
  title: string,
  initialName = ""
): Promise<string | null> {
  const result = await Swal.fire({
    title,
    input: "text",
    inputValue: initialName,
    inputPlaceholder: "e.g. Hardwood",
    inputAttributes: { maxlength: "100" },
    showCancelButton: true,
    confirmButtonColor: "#FFD400",
    cancelButtonColor: "#aaa",
    confirmButtonText: "Save",
    cancelButtonText: "Cancel",
    heightAuto: false,
    inputValidator: (value) => {
      if (!value || !value.trim()) return "Wood quality name is required.";
      if (value.trim().length > 100) return "Maximum 100 characters allowed.";
      return null;
    },
  });
  if (!result.isConfirmed) return null;
  return String(result.value || "").trim();
}

function parseCategoryPrice(raw: string): number | null {
  const value = String(raw ?? "").trim();
  if (value === "") return null;
  const price = Number(value);
  if (!Number.isFinite(price) || price < 0) return NaN;
  return price;
}

export async function promptAddWoodQuality(): Promise<{
  name: string;
  category_prices: CategoryQualityPrice[];
} | null> {
  let categories: ProductCategoryOption[] = [];
  try {
    categories = await fetchProductCategories();
  } catch {
    await Swal.fire({
      icon: "error",
      title: "Could not load product categories",
      text: "Wood quality prices are set per product category. Try again.",
      confirmButtonColor: "#FFD400",
      heightAuto: false,
    });
    return null;
  }

  const categoryFields = categories
    .map(
      (c) => `
        <div style="display:flex;align-items:center;gap:10px;margin:8px 0;">
          <label for="wq-price-${c.id}" style="flex:1;text-align:left;font-size:14px;color:#333;">${escapeHtml(c.name)}</label>
          <input id="wq-price-${c.id}" class="swal2-input" type="number" min="0" step="0.01"
            placeholder="Price" data-category-id="${c.id}"
            style="width:140px;margin:0;">
        </div>`
    )
    .join("");

  const result = await Swal.fire({
    title: "Add wood quality",
    width: 560,
    html: `
      <input id="wq-name" class="swal2-input" maxlength="100" placeholder="e.g. Hardwood" style="width:90%;">
      <div style="text-align:left;margin:12px 8px 6px;font-weight:600;color:#222;">Price per product category</div>
      <div style="max-height:280px;overflow:auto;padding:0 12px 8px;">
        ${
          categories.length === 0
            ? `<div style="color:#888;font-size:13px;">No product categories found. The quality will be added without prices.</div>`
            : categoryFields
        }
      </div>
    `,
    showCancelButton: true,
    confirmButtonColor: "#FFD400",
    cancelButtonColor: "#aaa",
    confirmButtonText: "Add",
    cancelButtonText: "Cancel",
    heightAuto: false,
    focusConfirm: false,
    preConfirm: () => {
      const name = (document.getElementById("wq-name") as HTMLInputElement | null)?.value?.trim() || "";
      if (!name) {
        Swal.showValidationMessage("Wood quality name is required.");
        return false;
      }
      if (name.length > 100) {
        Swal.showValidationMessage("Maximum 100 characters allowed.");
        return false;
      }
      const category_prices: CategoryQualityPrice[] = [];
      for (const category of categories) {
        const raw = (document.getElementById(`wq-price-${category.id}`) as HTMLInputElement | null)?.value || "";
        const price = parseCategoryPrice(raw);
        if (price === null) {
          Swal.showValidationMessage(`Enter a price for ${category.name}.`);
          return false;
        }
        if (Number.isNaN(price)) {
          Swal.showValidationMessage(`Enter a valid price of zero or more for ${category.name}.`);
          return false;
        }
        category_prices.push({ category_id: category.id, price });
      }
      return { name, category_prices };
    },
  });
  if (!result.isConfirmed || !result.value) return null;
  return result.value as { name: string; category_prices: CategoryQualityPrice[] };
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
